/**
 * File Preview Utilities
 *
 * Utilities for determining file preview capabilities and types
 */

export type PreviewType = 'image' | 'pdf' | 'text' | 'video' | 'audio' | 'none';

/**
 * Determine if a file can be previewed based on its MIME type
 */
export function isPreviewable(mimeType: string): boolean {
  return getPreviewType(mimeType) !== 'none';
}

/**
 * Get the preview type for a file based on its MIME type
 */
export function getPreviewType(mimeType: string): PreviewType {
  const type = mimeType.toLowerCase();

  // Images - including HEIC, HEIF, TIFF formats
  if (
    type.startsWith('image/') &&
    (type.includes('png') ||
      type.includes('jpg') ||
      type.includes('jpeg') ||
      type.includes('gif') ||
      type.includes('svg') ||
      type.includes('webp') ||
      type.includes('bmp') ||
      type.includes('tiff') ||
      type.includes('heic') ||
      type.includes('heif'))
  ) {
    return 'image';
  }

  // PDFs
  if (type === 'application/pdf') {
    return 'pdf';
  }

  // Text files
  if (
    type.startsWith('text/') ||
    type === 'application/json' ||
    type === 'application/xml' ||
    type === 'application/javascript' ||
    type === 'application/typescript'
  ) {
    return 'text';
  }

  // Videos
  if (
    type.startsWith('video/') &&
    (type.includes('mp4') ||
      type.includes('webm') ||
      type.includes('ogg') ||
      type.includes('quicktime'))
  ) {
    return 'video';
  }

  // Audio
  if (
    type.startsWith('audio/') &&
    (type.includes('mpeg') ||
      type.includes('mp3') ||
      type.includes('wav') ||
      type.includes('ogg') ||
      type.includes('webm') ||
      type.includes('mp4') ||
      type.includes('m4a'))
  ) {
    return 'audio';
  }

  return 'none';
}

/**
 * Get a user-friendly file type label
 */
export function getFileTypeLabel(mimeType: string, evidenceType?: string): string {
  const previewType = getPreviewType(mimeType);

  switch (previewType) {
    case 'image':
      return 'Image';
    case 'pdf':
      return 'PDF Document';
    case 'text':
      return 'Text File';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    default:
      // Fall back to evidence type if available
      if (evidenceType) {
        return evidenceType.charAt(0) + evidenceType.slice(1).toLowerCase();
      }
      return 'Document';
  }
}

/**
 * Format file size to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {return '0 Bytes';}

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get appropriate file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
}
